/**
 * Quotations API Client
 * Epic 25: Árajánlat kezelés
 */

import { api } from './client';

// ============================================
// TYPES (re-export from shared types)
// ============================================

export type QuotationStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'converted';

export interface QuotationItem {
  id: string;
  productId?: string;
  description: string;
  quantity: number;
  unit: string;
  unitPriceNet: number;
  vatRate: number;
  totalNet: number;
  totalGross: number;
}

export interface Quotation {
  id: string;
  quotationNumber: string;
  status: QuotationStatus;
  worksheetId?: string;
  worksheetNumber?: string;
  partnerId: string;
  partnerName: string;
  partnerEmail?: string;
  partnerPhone?: string;
  createdAt: string;
  sentAt?: string;
  validUntil: string;
  respondedAt?: string;
  items: QuotationItem[];
  subtotalNet: number;
  vatAmount: number;
  totalGross: number;
  // P-CSH-3: Advance payment
  advanceAmount?: number;
  advancePaymentMethod?: string;
  advancePaidAt?: string;
  advanceDeductedFromInvoice?: string;
  advanceRefundedAt?: string;
  // SRV-FG-2-2: QuoteVersion
  version?: number;
  parentQuoteId?: string;
  totalVersions?: number;
  notes?: string;
  internalNotes?: string;
  createdBy: string;
}

export interface QuotationListResponse {
  data: Quotation[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface QuotationResponse {
  data: Quotation;
}

export interface QuotationFilters {
  search?: string;
  status?: QuotationStatus;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

// ============================================
// BACKEND → FRONTEND NORMALIZATION
// ============================================

/** Default VAT rate — used ONLY when backend omits vatRate (should not happen in production) */
const DEFAULT_VAT_RATE_PERCENT = 27;

/** Normalize backend quote to frontend Quotation format */
function normalizeQuotation(raw: Record<string, unknown>): Quotation {
  const status = String(raw['status'] ?? 'draft').toLowerCase() as QuotationStatus;
  const subtotal = Number(raw['subtotal'] ?? raw['subtotalNet'] ?? 0);
  const vatAmount = Number(raw['vatAmount'] ?? 0);
  const totalAmount = Number(raw['totalAmount'] ?? raw['totalGross'] ?? 0);

  // Normalize items if present
  const rawItems = (raw['items'] as Record<string, unknown>[] | undefined) ?? [];
  const items: QuotationItem[] = rawItems.map(item => {
    // M-004 fix: Use item's actual VAT rate, not hardcoded 27%
    const itemVatRate =
      item['vatRate'] != null ? Number(item['vatRate']) : DEFAULT_VAT_RATE_PERCENT;
    if (item['vatRate'] == null) {
      console.warn(
        `[normalizeQuotation] Item "${String(item['id'] ?? 'unknown')}" missing vatRate, falling back to ${DEFAULT_VAT_RATE_PERCENT}%`
      );
    }
    const totalNet = Number(item['totalPrice'] ?? item['totalNet'] ?? 0);
    return {
      id: String(item['id'] ?? ''),
      productId: item['productId'] ? String(item['productId']) : undefined,
      description: String(item['description'] ?? ''),
      quantity: Number(item['quantity'] ?? 0),
      unit: String(item['unit'] ?? 'db'),
      unitPriceNet: Number(item['unitPrice'] ?? item['unitPriceNet'] ?? 0),
      vatRate: itemVatRate,
      totalNet,
      totalGross: Number(item['totalGross'] ?? Math.round(totalNet * (1 + itemVatRate / 100))),
    };
  });

  return {
    id: String(raw['id'] ?? ''),
    quotationNumber: String(raw['quoteNumber'] ?? raw['quotationNumber'] ?? ''),
    status,
    worksheetId: raw['worksheetId'] ? String(raw['worksheetId']) : undefined,
    worksheetNumber: raw['worksheetNumber'] ? String(raw['worksheetNumber']) : undefined,
    partnerId: String(raw['partnerId'] ?? ''),
    partnerName: String(raw['partnerName'] ?? 'Ismeretlen partner'),
    partnerEmail: raw['partnerEmail'] ? String(raw['partnerEmail']) : undefined,
    partnerPhone: raw['partnerPhone'] ? String(raw['partnerPhone']) : undefined,
    createdAt: String(raw['createdAt'] ?? ''),
    sentAt: raw['sentAt'] ? String(raw['sentAt']) : undefined,
    validUntil: String(raw['validUntil'] ?? ''),
    respondedAt: raw['respondedAt'] ? String(raw['respondedAt']) : undefined,
    items,
    subtotalNet: subtotal,
    vatAmount,
    totalGross: totalAmount || subtotal + vatAmount,
    // P-CSH-3: Advance payment fields
    advanceAmount: raw['advanceAmount'] ? Number(raw['advanceAmount']) : undefined,
    advancePaymentMethod: raw['advancePaymentMethod']
      ? String(raw['advancePaymentMethod'])
      : undefined,
    advancePaidAt: raw['advancePaidAt'] ? String(raw['advancePaidAt']) : undefined,
    advanceDeductedFromInvoice: raw['advanceDeductedFromInvoice']
      ? String(raw['advanceDeductedFromInvoice'])
      : undefined,
    advanceRefundedAt: raw['advanceRefundedAt'] ? String(raw['advanceRefundedAt']) : undefined,
    // SRV-FG-2-2: QuoteVersion fields
    version: raw['version'] != null ? Number(raw['version']) : undefined,
    parentQuoteId: raw['parentQuoteId'] ? String(raw['parentQuoteId']) : undefined,
    totalVersions: raw['totalVersions'] != null ? Number(raw['totalVersions']) : undefined,
    notes: raw['notes'] ? String(raw['notes']) : undefined,
    internalNotes:
      (raw['internalNotes'] ?? raw['introduction'])
        ? String(raw['internalNotes'] ?? raw['introduction'])
        : undefined,
    createdBy: String(raw['createdBy'] ?? ''),
  };
}

// ============================================
// API FUNCTIONS
// ============================================

export async function getQuotations(
  filters: QuotationFilters = {}
): Promise<QuotationListResponse> {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  // Kontraktus-map (QA-4 P0-3b): a BE ListQuotesQueryDto limit/offset-et whitelistel
  // (forbidNonWhitelisted!), a page/pageSize query-param 400-at dobott → a lista-oldal
  // error-state-be esett ("HTTP 400"). A page 1-alapú (use-server-pagination kontraktus).
  if (filters.pageSize !== undefined && filters.pageSize > 0) {
    params.append('limit', filters.pageSize.toString());
    if (filters.page !== undefined) {
      params.append('offset', Math.max(0, (filters.page - 1) * filters.pageSize).toString());
    }
  }

  const queryString = params.toString();
  const endpoint = queryString ? `/quotations?${queryString}` : '/quotations';

  const raw = await api.get<{ data: Record<string, unknown>[]; total?: number; meta?: unknown }>(
    endpoint
  );

  const data = (raw.data ?? []).map(normalizeQuotation);
  const total = Number(raw.total ?? data.length);
  const page = filters.page ?? 1;
  const pageSize = filters.pageSize ?? 20;

  return {
    data,
    meta: { total, page, pageSize, hasMore: total > page * pageSize },
  };
}

export async function getQuotationById(id: string): Promise<QuotationResponse> {
  const raw = await api.get<{ data: Record<string, unknown> }>(`/quotations/${id}`);
  return { data: normalizeQuotation(raw.data) };
}

export async function createQuotation(data: {
  partnerId: string;
  worksheetId?: string;
  validUntil: string;
  items: Omit<QuotationItem, 'id' | 'totalNet' | 'totalGross'>[];
  notes?: string;
}): Promise<QuotationResponse> {
  return api.post<QuotationResponse>('/quotations', data);
}

export async function updateQuotation(
  id: string,
  data: Partial<{
    status: QuotationStatus;
    validUntil: string;
    items: QuotationItem[];
    notes: string;
    internalNotes: string;
    subtotalNet: number;
    vatAmount: number;
    totalGross: number;
  }>
): Promise<QuotationResponse> {
  return api.patch<QuotationResponse>(`/quotations/${id}`, data);
}

export async function deleteQuotation(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/quotations/${id}`);
}

export async function sendQuotation(id: string): Promise<QuotationResponse> {
  return api.post<QuotationResponse>(`/quotations/${id}/send`, {});
}

export async function acceptQuotation(id: string): Promise<QuotationResponse> {
  return api.post<QuotationResponse>(`/quotations/${id}/accept`, {});
}

export async function rejectQuotation(
  id: string,
  rejectionReason?: string
): Promise<QuotationResponse> {
  return api.post<QuotationResponse>(`/quotations/${id}/reject`, { rejectionReason });
}

export interface ConvertToWorksheetResponse {
  data: {
    quote: Record<string, unknown>;
    worksheet: { id: string; worksheetNumber: string; status: string };
  };
  message: string;
}

export async function convertToWorksheet(id: string): Promise<ConvertToWorksheetResponse> {
  return api.post<ConvertToWorksheetResponse>(`/quotations/${id}/convert-to-worksheet`, {});
}

// ============================================
// QUOTE VERSION API (SRV-FG-2-2)
// ============================================

export interface QuotationVersionEntry {
  id: string;
  quotationNumber: string;
  version: number;
  status: QuotationStatus;
  totalGross: number;
  subtotalNet: number;
  createdAt: string;
  parentQuoteId?: string;
  partnerName?: string;
}

export interface QuotationVersionHistoryResponse {
  data: {
    versions: QuotationVersionEntry[];
    currentVersion: number;
    totalVersions: number;
  };
}

export async function getQuotationVersionHistory(
  id: string
): Promise<QuotationVersionHistoryResponse> {
  const raw = await api.get<{
    data: {
      versions: Record<string, unknown>[];
      currentVersion: number;
      totalVersions: number;
    };
  }>(`/quotations/${id}/versions`);

  const versions: QuotationVersionEntry[] = (raw.data.versions ?? []).map(v => ({
    id: String(v['id'] ?? ''),
    quotationNumber: String(v['quoteNumber'] ?? v['quotationNumber'] ?? ''),
    version: Number(v['version'] ?? 1),
    status: String(v['status'] ?? 'draft').toLowerCase() as QuotationStatus,
    totalGross: Number(v['totalAmount'] ?? v['totalGross'] ?? 0),
    subtotalNet: Number(v['subtotal'] ?? v['subtotalNet'] ?? 0),
    createdAt: String(v['createdAt'] ?? ''),
    parentQuoteId: v['parentQuoteId'] ? String(v['parentQuoteId']) : undefined,
    partnerName: v['partnerName'] ? String(v['partnerName']) : undefined,
  }));

  return {
    data: {
      versions,
      currentVersion: raw.data.currentVersion,
      totalVersions: raw.data.totalVersions,
    },
  };
}

export async function createNewQuotationVersion(id: string): Promise<QuotationResponse> {
  const raw = await api.post<{ data: Record<string, unknown>; message: string }>(
    `/quotations/${id}/new-version`,
    {}
  );
  return { data: normalizeQuotation(raw.data) };
}

// ============================================
// ADVANCE PAYMENT API (P-CSH-3)
// ============================================

export interface RecordAdvancePaymentInput {
  amount: number;
  paymentMethod: 'CASH' | 'CARD' | 'TRANSFER';
}

export async function recordAdvancePayment(
  id: string,
  data: RecordAdvancePaymentInput
): Promise<QuotationResponse> {
  const raw = await api.post<{ data: Record<string, unknown>; message: string }>(
    `/quotations/${id}/advance`,
    data
  );
  return { data: normalizeQuotation(raw.data) };
}

export async function refundAdvancePayment(id: string): Promise<QuotationResponse> {
  const raw = await api.post<{ data: Record<string, unknown>; message: string }>(
    `/quotations/${id}/advance/refund`,
    {}
  );
  return { data: normalizeQuotation(raw.data) };
}

export async function deductAdvanceFromInvoice(
  id: string,
  invoiceNumber: string
): Promise<QuotationResponse> {
  const raw = await api.post<{ data: Record<string, unknown>; message: string }>(
    `/quotations/${id}/advance/deduct`,
    { invoiceNumber }
  );
  return { data: normalizeQuotation(raw.data) };
}
